import React, {FC, useState, useCallback, useRef} from 'react'
import {useDispatch} from 'react-redux'
import {toast} from 'react-toastify'
import styles from './InboxV3.module.scss'
import {useInboxV3} from './hooks/useInboxV3'
import {useInboxResponsive} from '../inbox-v2/useInboxResponsive'
import LeftPanel from './Components/LeftPanel/LeftPanel'
import CenterPanel, {ChatMessageHelpers} from './Components/CenterPanel/CenterPanel'
import RightPanel from './Components/RightPanel/RightPanel'
import QuickFiltersModal from '../inbox-v2/Components/QuickFiltersModal'
import AssignedTags from '../Contacts/AllContactsList/Modal/AssignedTags'
import {useSendQMMutation, useLazyGetInboxMessageDetailsQuery} from '../../../../services/cellcast'
import {mapV2MessageToV3} from './services/inboxMapper'
import {QuickFilterTab} from './types/inbox-v3.types'
import {MessageFilter} from './hooks/useInboxFilters'
import {GetSingleRmsChat} from '../inbox-v2/InboxSlice/InboxSlice'

// RMS conversation type
interface RmsConversation {
  rmsThreadKey: string
  subscriberNumber: string
  message: string
  fields?: Array<Array<{name: string; value: string}>>
}

interface InboxV3Props {}

// Map quickFilter tabs to messageFilter values
const quickFilterToMessageFilter: Record<QuickFilterTab, MessageFilter> = {
  all: 'All Message',
  open: 'Active Messages Only',
  archived: 'Archived',
}

// Map messageFilter back to quickFilter for display
const messageFilterToQuickFilter = (filter: MessageFilter): QuickFilterTab => {
  switch (filter) {
    case 'Archived':
      return 'archived'
    case 'Active Messages Only':
      return 'open'
    default:
      return 'all'
  }
}

const InboxV3: FC<InboxV3Props> = () => {
  const dispatch = useDispatch()
  const {isMobile, isTablet} = useInboxResponsive()
  const [showFiltersModal, setShowFiltersModal] = useState(false)

  // RMS and Email state
  const [selectedRmsId, setSelectedRmsId] = useState<string | null>(null)
  const [selectedRmsNumber, setSelectedRmsNumber] = useState<string>('')

  // Tag modal state
  const [showTagModal, setShowTagModal] = useState(false)
  const [tagContactId, setTagContactId] = useState<string | null>(null)

  // Treat both mobile and tablet as mobile layout
  const isMobileLayout = isMobile || isTablet

  // RTK Query hooks for sending messages
  const [sendSMSInbox, {isLoading: isSendingMessage}] = useSendQMMutation()
  const [fetchInboxMessageDetails] = useLazyGetInboxMessageDetailsQuery()

  // Store message helpers from CenterPanel for prepending messages after send
  const messageHelpersRef = useRef<ChatMessageHelpers | null>(null)

  // Callback to receive message helpers from CenterPanel
  const handleMessageHelpersReady = useCallback((helpers: ChatMessageHelpers) => {
    messageHelpersRef.current = helpers
  }, [])

  const {
    // State
    chatType,
    searchQuery,
    messageFilter,
    sortOrder,
    assigneeFilter,
    searchMode,
    selectedConversationId,
    selectedConversation,
    selectedConversations,
    selectedReceiver,
    rightPanelTab,
    isLoading,
    isLoadingContact,
    mobileView,

    // Computed
    filteredConversations,
    contactDetails,
    contactStats,
    openCount,
    messengerName,
    teamMembers,
    isLoadingTeamMembers,
    assignedTo,
    isBulkActionsActive,

    // Actions
    setChatType,
    setSearchQuery,
    setMessageFilter,
    toggleSortOrder,
    applyFilters,
    clearFilters,
    setRightPanelTab,
    selectConversation,
    toggleConversationSelection,
    archiveConversation,
    flagConversation,
    assignConversation,
    unassignConversation,
    unsubscribeConversation,
    subscribeConversation,
    goBackToList,
    openContactDetails,
    closeContactDetails,
    toggleRightPanel,
    showRightPanel,
    refetchContactDetails,
  } = useInboxV3()

  // Actual send message function using RTK Query
  // Now receives sender and senderType from MessageInputV3
  const sendMessage = useCallback(
    (receiver: string, message: string, sender: string, senderType: string, longUrls?: any[]) => {
      if (!receiver || !message.trim()) {
        toast.error('Please enter a message')
        return
      }

      const payload: any = {
        sender: sender,
        message: message.trim(),
        senderType: senderType,
        contacts: [receiver],
        countryCode: '61',
      }

      // Add long URLs if present
      if (longUrls && longUrls.length > 0) {
        payload.longUrl = longUrls
      }

      sendSMSInbox(payload)
        .unwrap()
        .then((res: any) => {
          const messageId = res?.data?.queueResponse?.at(0)?.MessageId
          if (messageId) {
            // Fetch the sent message details and prepend to the list (like inbox-v2)
            // This allows the message to appear immediately with status tracking via long polling
            fetchInboxMessageDetails(messageId)
              .unwrap()
              .then((messageRes: any) => {
                // Map the V2 message format to V3 format and prepend
                const messageData = messageRes?.data || messageRes
                if (messageData && messageHelpersRef.current?.prependMessage) {
                  const mappedMessage = mapV2MessageToV3(messageData)
                  messageHelpersRef.current.prependMessage(mappedMessage)
                }
                toast.success('Message sent successfully')
              })
              .catch(() => {
                // Message was sent, just couldn't fetch details
                // Long polling will pick it up on next interval
                toast.success('Message sent successfully')
              })
          } else {
            toast.success('Message sent successfully')
          }
        })
        .catch((error: any) => {
          console.error('Send message error:', error)
          toast.error(error?.data?.message || 'Failed to send message')
        })
    },
    [sendSMSInbox, fetchInboxMessageDetails]
  )

  const scheduleMessage = useCallback(
    (
      receiver: string,
      message: string,
      sender: string,
      senderType: string,
      scheduleAt: Date,
      longUrls?: any[]
    ) => {
      if (!receiver || !message.trim()) {
        toast.error('Please enter a message')
        return
      }

      const payload: any = {
        sender: sender,
        message: message.trim(),
        senderType: senderType,
        contacts: [receiver],
        countryCode: '61',
        scheduleAt,
      }

      if (longUrls && longUrls.length > 0) {
        payload.longUrl = longUrls
      }

      sendSMSInbox(payload)
        .unwrap()
        .then((res: any) => {
          toast.success('Message scheduled successfully')
        })
        .catch((error: any) => {
          console.error('Schedule message error:', error)
          toast.error(error?.data?.message || 'Failed to schedule message')
        })
    },
    [sendSMSInbox]
  )

  // Build container classes for mobile
  const containerClasses = [styles.inboxV3Container, isMobileLayout && styles.mobileLayout]
    .filter(Boolean)
    .join(' ')

  // Build panel classes for mobile view state
  const leftPanelClasses = [
    styles.leftPanel,
    isMobileLayout && mobileView !== 'list' && styles.hidden,
  ]
    .filter(Boolean)
    .join(' ')

  const centerPanelClasses = [
    styles.centerPanel,
    isMobileLayout && mobileView === 'chat' && styles.active,
  ]
    .filter(Boolean)
    .join(' ')

  const rightPanelClasses = [
    styles.rightPanel,
    showRightPanel && styles.visible,
    isMobileLayout && mobileView === 'details' && styles.active,
  ]
    .filter(Boolean)
    .join(' ')

  // Handle filter modal
  const handleFilterClick = () => {
    setShowFiltersModal(true)
  }

  // Handle RMS conversation selection
  const handleRmsSelect = useCallback(
    (conversation: RmsConversation) => {
      setSelectedRmsId(conversation.rmsThreadKey)
      setSelectedRmsNumber(conversation.subscriberNumber)
      // Fetch RMS single chat data
      dispatch(GetSingleRmsChat(conversation.rmsThreadKey) as any)
    },
    [dispatch]
  )

  // Handle tag conversation - opens tag modal with correct contact ID
  const handleTagConversation = useCallback(
    (conversationId: string) => {
      // Find the conversation to get the contactgroupmaps._id
      const conversation = filteredConversations.find((c) => c._id === conversationId)
      if (conversation?.contactgroupmaps?._id) {
        setTagContactId(conversation.contactgroupmaps._id)
        setShowTagModal(true)
      } else {
        toast.error('Unable to find contact for tagging')
      }
    },
    [filteredConversations]
  )

  // Handle tag modal close
  const handleTagModalClose = useCallback(() => {
    setShowTagModal(false)
    setTagContactId(null)
  }, [])

  // Handle chat type change - clear selections when switching tabs
  const handleChatTypeChange = useCallback(
    (type: typeof chatType) => {
      setChatType(type)
      // Clear selections when switching tabs
      setSelectedRmsId(null)
      setSelectedRmsNumber('')
    },
    [setChatType]
  )

  return (
    <div className={containerClasses}>
      {/* Left Panel - Conversation List */}
      <div className={leftPanelClasses}>
        <LeftPanel
          chatType={chatType}
          onChatTypeChange={handleChatTypeChange}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          quickFilter={messageFilterToQuickFilter(messageFilter)}
          onQuickFilterChange={(tab) => setMessageFilter(quickFilterToMessageFilter[tab])}
          sortOrder={sortOrder}
          onSortOrderChange={toggleSortOrder}
          onFilterClick={handleFilterClick}
          conversations={filteredConversations}
          selectedConversationId={selectedConversationId}
          selectedConversations={selectedConversations}
          onConversationSelect={toggleConversationSelection}
          onConversationClick={selectConversation}
          onArchive={archiveConversation}
          onFlag={flagConversation}
          onAssign={assignConversation}
          onTag={handleTagConversation}
          onUnsubscribe={unsubscribeConversation}
          onSubscribe={subscribeConversation}
          teamMembers={teamMembers}
          isBulkActionsActive={isBulkActionsActive}
          openCount={openCount}
          isLoading={isLoading}
          // RMS props
          selectedRmsId={selectedRmsId}
          onRmsSelect={handleRmsSelect}
        />
      </div>

      {/* Center Panel - Chat/Messages */}
      <div className={centerPanelClasses}>
        <CenterPanel
          chatType={chatType}
          selectedReceiver={selectedReceiver}
          selectedConversation={selectedConversation}
          selectedConversationId={selectedConversationId}
          messengerName={messengerName}
          onBack={goBackToList}
          isMobile={isMobileLayout}
          teamMembers={teamMembers}
          isLoadingTeamMembers={isLoadingTeamMembers}
          assignedTo={assignedTo}
          onArchive={archiveConversation}
          onFlag={flagConversation}
          onAssign={assignConversation}
          onUnassign={unassignConversation}
          onTag={handleTagConversation}
          onUnsubscribe={unsubscribeConversation}
          onSubscribe={subscribeConversation}
          onSendMessage={sendMessage}
          onSendScheduleMessage={scheduleMessage}
          isSendingMessage={isSendingMessage}
          onToggleRightPanel={toggleRightPanel}
          isRightPanelOpen={showRightPanel}
          onMessageHelpersReady={handleMessageHelpersReady}
          // RMS props
          selectedRmsId={selectedRmsId}
          selectedRmsNumber={selectedRmsNumber}
        />
      </div>

      {/* Right Panel - Contact Details */}
      <div className={rightPanelClasses}>
        <RightPanel
          contactDetails={contactDetails}
          contactStats={contactStats}
          activeTab={rightPanelTab}
          onTabChange={setRightPanelTab}
          isLoading={isLoadingContact}
          isMobile={isMobileLayout}
          onClose={closeContactDetails}
          onTagsUpdated={refetchContactDetails}
        />
      </div>

      {/* Mobile overlay for right panel */}
      {isMobileLayout && mobileView === 'details' && (
        <div
          className={`${styles.mobileOverlay} ${styles.active}`}
          onClick={closeContactDetails}
        ></div>
      )}

      {/* Filters Modal */}
      <QuickFiltersModal
        show={showFiltersModal}
        onHide={() => setShowFiltersModal(false)}
        onApplyFilters={(filters: any) => {
          // Apply filters from modal to conversation list
          applyFilters({
            searchType: filters.searchType,
            assignedTo: filters.assignedTo,
            messageFilter: filters.messageFilter,
            sortOrder: filters.sortOrder,
          })
          setShowFiltersModal(false)
        }}
        onClearFilters={() => {
          // Clear all filters and reset to defaults
          clearFilters()
        }}
        teamMembers={teamMembers}
        currentFilters={{
          searchType: searchMode,
          assignedTo: assigneeFilter,
          messageFilter: messageFilter,
          sortOrder: sortOrder,
        }}
      />

      {/* Tag Modal */}
      <AssignedTags
        show={showTagModal}
        handleClose={handleTagModalClose}
        checkList={tagContactId ? [tagContactId] : []}
        callbackFn={() => {
          // Refresh contact details after tagging
          refetchContactDetails?.()
        }}
      />
    </div>
  )
}

export default InboxV3